import type {
  DetailMonthlyRow,
  DetailUserRow,
  MonthlyResourceRow,
  ProjectDetailStats,
  ProjectGoldFactRow,
  UserMonthBreakdownRow,
} from '../types/projectAnalysis'
import type { PeriodFilter } from './projectAnalysisPeriod'
import { isRecordInPeriod } from './projectAnalysisPeriod'

function pf(v: string | number | undefined | null): number {
  if (v == null) return 0
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Number.isFinite(n) ? n : 0
}

function pi(v: string | number | undefined | null): number {
  if (v == null) return 0
  const n = typeof v === 'string' ? parseInt(v, 10) : v
  return Number.isFinite(n) ? n : 0
}

type MonthAcc = {
  year: number
  month: number
  name: string
  total_hours: number
  billable_hours: number
  non_billable_hours: number
  entries_count: number
  late_entries_count: number
  leave_days: number
}

export function computeProjectDetailStats(
  projectRawData: ProjectGoldFactRow[],
  detailFilter: PeriodFilter,
): ProjectDetailStats | null {
  if (!projectRawData?.length) return null
  const filtered = projectRawData.filter((d) =>
    isRecordInPeriod(d.year, d.month, detailFilter),
  )
  if (filtered.length === 0) return null

  const totalHours = filtered.reduce((s, d) => s + pf(d.total_hours), 0)
  const billableHours = filtered.reduce((s, d) => s + pf(d.billable_hours), 0)
  const nonBillableHours = filtered.reduce(
    (s, d) => s + pf(d.non_billable_hours),
    0,
  )
  const totalEntries = filtered.reduce((s, d) => s + pi(d.entries_count), 0)
  const lateEntries = filtered.reduce((s, d) => s + pi(d.late_entries_count), 0)
  const pendingSignoffs = filtered.reduce(
    (s, d) => s + pi(d.pending_signoff_entries),
    0,
  )

  const userMap = new Map<number, DetailUserRow>()
  filtered.forEach((row) => {
    const uid = row.user_id
    if (!userMap.has(uid)) {
      userMap.set(uid, {
        user_id: uid,
        full_name: row.full_name,
        username: row.username,
        user_is_active: row.user_is_active,
        total_hours: 0,
        billable_hours: 0,
        non_billable_hours: 0,
        entries_count: 0,
        late_entries_count: 0,
        leave_days: 0,
      })
    }
    const u = userMap.get(uid)!
    u.total_hours += pf(row.total_hours)
    u.billable_hours += pf(row.billable_hours)
    u.non_billable_hours += pf(row.non_billable_hours)
    u.entries_count += pi(row.entries_count)
    u.late_entries_count += pi(row.late_entries_count)
    u.leave_days += pf(row.total_leaves_days ?? row.total_leave_days)
  })
  const usersData = Array.from(userMap.values()).sort(
    (a, b) => b.total_hours - a.total_hours,
  )

  const userMonthlyBreakdownByUser = new Map<number, Map<string, MonthAcc>>()
  filtered.forEach((row) => {
    const uid = row.user_id
    const ymKey = `${row.year}-${String(row.month).padStart(2, '0')}`
    if (!userMonthlyBreakdownByUser.has(uid))
      userMonthlyBreakdownByUser.set(uid, new Map())
    const monthMap = userMonthlyBreakdownByUser.get(uid)!
    if (!monthMap.has(ymKey)) {
      monthMap.set(ymKey, {
        year: row.year,
        month: row.month,
        name: ymKey,
        total_hours: 0,
        billable_hours: 0,
        non_billable_hours: 0,
        entries_count: 0,
        late_entries_count: 0,
        leave_days: 0,
      })
    }
    const m = monthMap.get(ymKey)!
    m.total_hours += pf(row.total_hours)
    m.billable_hours += pf(row.billable_hours)
    m.non_billable_hours += pf(row.non_billable_hours)
    m.entries_count += pi(row.entries_count)
    m.late_entries_count += pi(row.late_entries_count)
    m.leave_days += pf(row.total_leaves_days ?? row.total_leave_days)
  })
  const userMonthlyBreakdown = new Map<number, UserMonthBreakdownRow[]>()
  userMonthlyBreakdownByUser.forEach((monthMap, uid) => {
    const list: UserMonthBreakdownRow[] = Array.from(monthMap.values()).map(
      (m) => ({
        year: m.year,
        month: m.month,
        name: m.name,
        total_hours: Math.round(m.total_hours * 100) / 100,
        billable_hours: Math.round(m.billable_hours * 100) / 100,
        non_billable_hours: Math.round(m.non_billable_hours * 100) / 100,
        entries_count: m.entries_count,
        late_entries_count: m.late_entries_count,
        leave_days: Math.round((m.leave_days ?? 0) * 10) / 10,
        util:
          m.total_hours > 0
            ? Math.round((m.billable_hours / m.total_hours) * 1000) / 10
            : 0,
      }),
    )
    list.sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.month - b.month,
    )
    userMonthlyBreakdown.set(uid, list)
  })

  const monthlyResourceBreakdown = new Map<string, MonthlyResourceRow[]>()
  usersData.forEach((u) => {
    const rows = userMonthlyBreakdown.get(u.user_id)
    if (!rows?.length) return
    for (const r of rows) {
      if (!monthlyResourceBreakdown.has(r.name))
        monthlyResourceBreakdown.set(r.name, [])
      monthlyResourceBreakdown.get(r.name)!.push({
        user_id: u.user_id,
        full_name: u.full_name,
        username: u.username,
        total_hours: r.total_hours,
        billable_hours: r.billable_hours,
        non_billable_hours: r.non_billable_hours,
        entries_count: r.entries_count ?? 0,
        late_entries_count: r.late_entries_count ?? 0,
        leave_days: r.leave_days,
        util: r.util,
      })
    }
  })
  monthlyResourceBreakdown.forEach((list) => {
    list.sort((a, b) => b.total_hours - a.total_hours)
  })

  type MonthAggInternal = {
    year: number
    month: number
    name: string
    total_hours: number
    billable_hours: number
    non_billable_hours: number
    entries_count: number
    late_entries_count: number
    leave_days: number
    unique_users: Set<number>
  }
  const monthMapAgg = new Map<string, MonthAggInternal>()
  filtered.forEach((row) => {
    const key = `${row.year}-${String(row.month).padStart(2, '0')}`
    if (!monthMapAgg.has(key)) {
      monthMapAgg.set(key, {
        year: row.year,
        month: row.month,
        name: key,
        total_hours: 0,
        billable_hours: 0,
        non_billable_hours: 0,
        entries_count: 0,
        late_entries_count: 0,
        leave_days: 0,
        unique_users: new Set(),
      })
    }
    const m = monthMapAgg.get(key)!
    m.total_hours += pf(row.total_hours)
    m.billable_hours += pf(row.billable_hours)
    m.non_billable_hours += pf(row.non_billable_hours)
    m.entries_count += pi(row.entries_count)
    m.late_entries_count += pi(row.late_entries_count)
    m.leave_days += pf(row.total_leaves_days ?? row.total_leave_days)
    m.unique_users.add(row.user_id)
  })
  const monthlyData: DetailMonthlyRow[] = Array.from(monthMapAgg.values())
    .map((m) => ({
      year: m.year,
      month: m.month,
      name: m.name,
      total_hours: m.total_hours,
      billable_hours: m.billable_hours,
      non_billable_hours: m.non_billable_hours,
      entries_count: m.entries_count,
      late_entries_count: m.late_entries_count,
      leave_days: m.leave_days,
      unique_users: m.unique_users.size,
    }))
    .sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.month - b.month,
    )

  const totalLeaveDays = filtered.reduce(
    (s, d) => s + pf(d.total_leaves_days ?? d.total_leave_days),
    0,
  )

  const first = projectRawData[0]
  return {
    projectInfo: {
      project_id: first.project_id ?? 0,
      project_name: first.project_name,
      project_category: first.project_category,
      project_type: first.project_type,
    },
    totalHours: Math.round(totalHours * 100) / 100,
    billableHours: Math.round(billableHours * 100) / 100,
    nonBillableHours: Math.round(nonBillableHours * 100) / 100,
    utilizationRate:
      totalHours > 0
        ? Math.round((billableHours / totalHours) * 10000) / 100
        : 0,
    totalEntries,
    lateEntries,
    lateEntriesRate:
      totalEntries > 0
        ? Math.round((lateEntries / totalEntries) * 1000) / 10
        : 0,
    pendingSignoffs,
    totalLeaveDays: Math.round(totalLeaveDays * 10) / 10,
    uniqueResources: userMap.size,
    usersData,
    userMonthlyBreakdown,
    monthlyResourceBreakdown,
    monthlyData,
  }
}
